import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';

import { Typography2 } from '@coursera/cds-core';
import { Col, Loading, Row } from '@coursera/coursera-ui';

import { Heading, Spacer } from 'bundles/enterprise-ui/components';
import PrivateAuthoringExistingPartner from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PrivateAuthoringExistingPartner';
import PrivateAuthoringNewPartner from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PrivateAuthoringNewPartner';
import PrivateAuthoringPartner from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PrivateAuthoringPartner';
import type {
  PrivateAuthoringQuery,
  PrivateAuthoringQueryVariables,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/PrivateAuthoringQuery';

import _t from 'i18n!nls/internal-enterprise';

type PropsFromCaller = {
  thirdPartyOrganizationId?: string;
};

type PropsFromGraphql = {
  data?: DataValue<PrivateAuthoringQuery, PrivateAuthoringQueryVariables>;
};

type Props = PropsFromCaller & PropsFromGraphql;

const PrivateAuthoring: React.FC<Props> = ({ data, thirdPartyOrganizationId }) => {
  if (!data || data.loading || !thirdPartyOrganizationId) {
    return <Loading />;
  }
  return (
    <Row data-testid="private-authoring">
      <Col md={3}>
        <Heading>{_t('Linked Educator organizations')}</Heading>
        {data?.EnterpriseAuthoringMappingsV1Resource?.byThirdPartyOrganizationId?.elements.length === 0 && (
          <div>
            <Typography2 component="p">{_t('This organization has no authoring partners mapped.')}</Typography2>
            <Spacer size={32} />
          </div>
        )}
        {data?.EnterpriseAuthoringMappingsV1Resource?.byThirdPartyOrganizationId?.elements.map((mapping) => {
          return (
            <div key={mapping?.id}>
              <PrivateAuthoringPartner partner={mapping?.partner} />
              <Spacer size={32} />
            </div>
          );
        })}
        <small>{_t('This private authoring configuration can only be removed or modified by Engineering.')}</small>
      </Col>
      <Col md={3}>
        <Heading>{_t('Use content from the following Educator organization')}</Heading>
        <Spacer size={24} />
        <PrivateAuthoringExistingPartner
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          onPartnerAdded={data.refetch}
        />
      </Col>
      <Col md={3}>
        <Heading>{_t('Create new Educator organization')}</Heading>
        <Spacer size={24} />
        <PrivateAuthoringNewPartner
          thirdPartyOrganizationId={thirdPartyOrganizationId}
          onPartnerCreated={data.refetch}
        />
      </Col>
    </Row>
  );
};

export const QueryForPrivateAuthoring = gql`
  query PrivateAuthoringQuery($thirdPartyOrganizationId: String!) {
    EnterpriseAuthoringMappingsV1Resource {
      byThirdPartyOrganizationId(orgId: $thirdPartyOrganizationId) {
        elements {
          id
          partner {
            id
            name
            shortName
            squareLogo
            partnerType
            display
          }
        }
      }
    }
  }
`;

export default graphql<PropsFromCaller, PrivateAuthoringQuery, PrivateAuthoringQueryVariables, PropsFromGraphql>(
  QueryForPrivateAuthoring,
  {
    skip: (props) => !props.thirdPartyOrganizationId,
    options: (props) => ({
      variables: { thirdPartyOrganizationId: props.thirdPartyOrganizationId! },
    }),
  }
)(PrivateAuthoring);
